import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  Dialog,
  Menu,
  MenuItem,
  Radio,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import HalfSheet from '../../components/HalfSheet';
import Icon from '../../components/Icon';
import ResizableSidebar from '../../components/ResizableSidebar';
import RightColumn from '../../components/RightColumn';
import SidebarToggle from '../../components/SidebarToggle';
import {
  CentreColumn,
  RIGHT_SIDEBAR_X,
  RightSidebar,
  RightSidebarBody,
  SidebarTop,
  StripLinks,
  TopStrip,
  rightSidebarHolds,
} from '../../components/Frame';
import { TOGGLE_BAND } from '../../components/viewToggleMetrics';
import DayDetail from './DayDetail';
import DaySwipe from './DaySwipe';
import PersonalEventForm from './PersonalEventForm';
import { emptyDraft, isComplete, slotFor, type PersonalDraft } from './personalEvents';
import { useIsMobile } from '../../shared/useIsMobile';
import type { AttendanceStore } from './useAttendance';
import type { LegChoiceStore } from './useLegChoices';
import type { HomeStore } from './useHome';
import MapMyDay from './MapMyDay';
import WeekGrid from './WeekGrid';
import { COLOR_OPTIONS, legendFor, type ColorKey } from './colorBy';
import ClearScheduleDialog, { type ClearScope } from './ClearScheduleDialog';
import { isWarningStat, useWeekStats, WEEK_STAT_ROWS } from './weekStats';
import { buildIcs, downloadIcs } from './ics';
import ShareMenu from '../share/ShareMenu';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { CORE_DAYS, longDate } from '../../shared/taxonomy';
import { localDate, type Opportunity } from '../../shared/opportunity';

/**
 * My Schedule: the week at a glance, and one day in detail.
 *
 * The panel on the right is persistent across both and can be collapsed in
 * either. It carries the week's totals and the color key. From 900px up it is
 * the page's right sidebar, full height; below that, a resizable panel beside
 * the week. A phone has none.
 */

const SIDEBAR_BG = '#292420';

/** The phone's personal event form, inside the half-up sheet. */
const FormSheet = styled('div')({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  color: BRAND.parchment,
});

/** Cancel, the heading and Save, pinned where half height can still reach them. */
const FormBar = styled('div')({
  flexShrink: 0,
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  gap: 8,
  padding: '12px 14px',
  borderBottom: '1px solid rgba(243,238,231,0.16)',
});

const FormBarTitle = styled('h2')({
  margin: 0,
  fontFamily: FONTS.display,
  fontSize: 15.5,
  fontWeight: 700,
  whiteSpace: 'nowrap',
});

const BarClose = styled('button')({
  justifySelf: 'start',
  appearance: 'none',
  background: 'none',
  border: 'none',
  width: 38,
  height: 38,
  marginLeft: -6,
  display: 'grid',
  placeItems: 'center',
  cursor: 'pointer',
  color: BRAND.parchment,
});

const BarSave = styled('button')({
  justifySelf: 'end',
  appearance: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '8px 18px',
  borderRadius: 999,
  fontFamily: FONTS.body,
  fontSize: 14,
  fontWeight: 700,
  background: BRAND.parchment,
  color: BRAND.mocha,
  '&:disabled': { background: 'rgba(243,238,231,0.25)', color: 'rgba(54,47,42,0.5)', cursor: 'default' },
});

const FormScroll = styled('div')({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  // The last fields, the summary and Delete clear a phone's toggle band.
  padding: '18px 20px calc(28px + var(--toggle-band, 0px))',
});

const Wrap = styled('div')(({ theme }) => ({
  position: 'relative',
  height: '100%',
  display: 'flex',
  minHeight: 0,
  padding: '10px 22px 18px',
  // From 900px up the columns run to the page's edges; the week keeps this
  // padding inside its own column.
  [theme.breakpoints.up('md')]: { padding: 0 },
  // A phone gives the week every pixel it has; eight columns need them.
  [theme.breakpoints.down('sm')]: { padding: '4px 10px 0' },
}));

/** The week's column under the top strip, from 900px up. */
const MainPad = styled('div')({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  padding: '10px 22px 18px',
});

/**
 * The week fills the width the sidebar leaves, minus a margin on each side.
 * It used to cap at 1180px, which left the calendar marooned in the middle of a
 * wide window — the days should grow with the viewport, not sit at a fixed size.
 */
const Centered = styled('div')(({ theme }) => ({
  width: '100%',
  padding: '0 50px',
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: { padding: 0 },
}));

const DangerBtn = styled('button')({
  appearance: 'none',
  cursor: 'pointer',
  padding: '7px 14px',
  borderRadius: 999,
  fontFamily: FONTS.body,
  fontSize: 12.5,
  fontWeight: 700,
  background: 'transparent',
  color: BRAND.parchment,
  border: '1px solid rgba(243,238,231,0.42)',
  '&:hover': { borderColor: '#ef3d23', color: '#ef3d23' },
  '&:disabled': { opacity: 0.3, cursor: 'default' },
});

const Main = styled('div')({
  position: 'relative',
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
});

/**
 * The schedule's own controls. Raised above the day-detail scrim (z 20) so the
 * sidebar toggle and the color filter keep working while a day is open, which
 * is how they read in "calendar focused background v2.jpg".
 */
const Head = styled('div')(({ theme }) => ({
  position: 'relative',
  zIndex: 25,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 12,
  marginBottom: 16,
  flexShrink: 0,
  // A phone's row never wraps: the two buttons and the lens are one row at
  // every width, and the buttons give up letters before the row gives up a line.
  [theme.breakpoints.down('sm')]: { gap: 6, marginBottom: 10, flexWrap: 'nowrap' },
}));

/**
 * The two buttons and the lens, from 600px up, held together: where the row is
 * too narrow for them beside the title they move under it as one, rather than
 * the lens being pushed off the row's end and onto the panel. A phone lays them
 * out in the row itself.
 */
const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 12,
  marginLeft: 'auto',
  [theme.breakpoints.down('sm')]: { display: 'contents' },
}));

const Title = styled('h1')({
  margin: 0,
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: 30,
  color: BRAND.parchment,
});

const IconBtn = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 5,
  display: 'grid',
  placeItems: 'center',
  color: BRAND.parchment,
  '&:hover': { opacity: 0.75 },
  // Held out of the way while the event form is up, and saying so.
  '&:disabled': { opacity: 0.3, cursor: 'default' },
  '&:disabled:hover': { opacity: 0.3 },
});

const Stat = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  gap: 10,
  padding: '6px 0',
  fontFamily: FONTS.body,
  fontSize: 12.5,
  borderBottom: '1px solid rgba(243,238,231,0.1)',
});

const SectionLabel = styled('div')({
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: '0.13em',
  opacity: 0.5,
  margin: '0 0 12px',
  fontFamily: FONTS.body,
});

/** A plain +, drawn rather than pulled from the brand's icon set. */
function PlusGlyph() {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M11 4a1 1 0 1 1 2 0v7h7a1 1 0 1 1 0 2h-7v7a1 1 0 1 1-2 0v-7H4a1 1 0 1 1 0-2h7V4Z"
      />
    </svg>
  );
}

/**
 * The phone's own +, for the round button: bigger than the row's, and drawn
 * with the slight taper the Sept 19 reference gives its tips rather than the
 * flat bars of `PlusGlyph`.
 */
function BigPlusGlyph({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="#fff"
        d="M11.05 0.9h1.9l0.36 3.1v16l-0.36 3.1h-1.9l-0.36-3.1v-16Z"
      />
      <path
        fill="#fff"
        d="M0.9 11.05v1.9l3.1 0.36h16l3.1-0.36v-1.9l-3.1-0.36h-16Z"
      />
    </svg>
  );
}

/** Add and Share in the top strip where the labels no longer fit: outlined like `ExportBtn`. */
const RoundStripBtn = styled('button')({
  appearance: 'none',
  cursor: 'pointer',
  padding: 0,
  width: 34,
  height: 34,
  borderRadius: 999,
  display: 'grid',
  placeItems: 'center',
  background: 'transparent',
  color: BRAND.parchment,
  border: '1px solid rgba(243,238,231,0.42)',
  '&:hover': { borderColor: BRAND.parchment, background: 'rgba(243,238,231,0.07)' },
  '&:disabled': { opacity: 0.35, cursor: 'default' },
});

/** The two circles beside a phone's thumb: Add below, the colour lens above. */
const RoundBtn = styled('button')({
  position: 'absolute',
  appearance: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'grid',
  placeItems: 'center',
  width: 52.6,
  height: 52.6,
  borderRadius: 999,
  background: 'rgba(11,9,9,0.75)',
  color: BRAND.parchment,
  right: 'calc(12px + env(safe-area-inset-right, 0px))',
  // Above the day's own layers (the heading 30, the button row 25) and below
  // the callout sheets (1199/1200), which are meant to cover them.
  zIndex: 40,
  WebkitTapHighlightColor: 'transparent',
  '&:disabled': { opacity: 0.35, cursor: 'default' },
});

const ExportBtn = styled('button', { shouldForwardProp: (p) => p !== '$keep' })<{
  /**
   * Keeps the full size under 600px. The narrow-desktop button row still needs
   * the smaller one, so this is a prop rather than a change to the rule.
   */
  $keep?: boolean;
}>(({ theme, $keep }) => ({
  appearance: 'none',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  padding: '8px 15px',
  borderRadius: 999,
  fontFamily: FONTS.body,
  fontSize: 12.5,
  whiteSpace: 'nowrap',
  // Both buttons and the lens share one row on a phone, so both give ground.
  ...($keep ? null : { [theme.breakpoints.down('sm')]: { padding: '7px 10px', fontSize: 11.5, gap: 5 } }),
  fontWeight: 700,
  background: 'transparent',
  color: BRAND.parchment,
  border: '1px solid rgba(243,238,231,0.42)',
  '&:hover': { borderColor: BRAND.parchment, background: 'rgba(243,238,231,0.07)' },
  '&:disabled': { opacity: 0.35, cursor: 'default' },
}));

export interface ScheduleHandle {
  /** The form sheet's own Cancel, for a view change or the account page. */
  cancelFormSheet: () => void;
}

interface Props {
  ref?: Ref<ScheduleHandle>;
  scheduled: Opportunity[];
  saved: Opportunity[];
  savedIds: Set<string>;
  onRemove: (id: string) => void;
  /** Moves an event between the schedule and Save for Later. */
  onToggleList: (id: string) => void;
  onClear: (scope: ClearScope) => void;
  /**
   * From 900px up, the page's own chrome, which this view places: the lockup
   * over the week, and About Us / Submit Feedback at the top of the panel, or
   * over the week when the panel is closed. The view toggle is the page's, drawn
   * over both views in one place.
   */
  chrome?: { lockup: ReactNode; links: ReactNode };
  /**
   * The phone chrome: the toggle is at the bottom of the screen, Share has
   * moved into the header and Add and the lens are round buttons beside the
   * thumb. A desktop window under 600px keeps today's button row.
   */
  phoneChrome?: boolean;
  /** Where to draw Share on a phone: App's header cell. */
  shareSlot?: HTMLElement | null;
  /** How much of each event the user is attending. Owned by App. */
  attendance: AttendanceStore;
  /**
   * Where each travel block sits and how the user said they were travelling.
   * Owned by App, which the callout's day and journeys read from as well.
   */
  legChoices: LegChoiceStore;
  /** `from` is 'day' for a card tapped in the day view, which opens the callout half up below 900px. */
  onOpenEvent: (event: Opportunity, from?: 'day') => void;
  /** The callout is half up over the day: the row of buttons slides away, and the day gets room to scroll. */
  calloutPeek?: boolean;
  /**
   * From 900px up, the callout, wherever it is drawn as the right sidebar. It
   * takes the panel's place while it is open, open or not, and closing it puts
   * back whichever it was.
   */
  callout?: ReactNode;
  /** The callout is open, and is the right sidebar. */
  calloutOpen?: boolean;
  /** The panel's toggle, pressed while the callout is in its place: the callout closes with it. */
  onCloseCallout?: () => void;
  /** Where the user is staying, for MAP MY DAY under the totals while a day is open. */
  home: HomeStore;
  /** The phone's personal event form is half up over the day, which App answers by sliding its header away. */
  onFormSheet?: (open: boolean) => void;
  /** Set when arriving via "Go to My Schedule"; drives the reveal sequence. */
  pendingReveal: { id: string; dayKey: string } | null;
  /** Set when a mini day was clicked: open this day, with this minute in the middle of the view. */
  pendingDay?: { dayKey: string; minute: number } | null;
  onPendingDayDone?: () => void;
  onRevealDone: () => void;
  /** The user's own events, as the form holds them. */
  personalDrafts: PersonalDraft[];
  onSavePersonal: (draft: PersonalDraft) => void;
  onDeletePersonal: (id: string) => void;
  /** Moving a personal event's edges in the day view. Saved without a toast. */
  onRetimePersonal: (id: string, startMinutes: number, endMinutes: number) => void;
}

export default function Schedule({
  ref,
  scheduled,
  saved,
  savedIds,
  onRemove,
  onToggleList,
  onClear,
  chrome,
  phoneChrome = false,
  shareSlot = null,
  attendance,
  legChoices,
  onOpenEvent,
  calloutPeek = false,
  callout,
  calloutOpen = false,
  onCloseCallout,
  home,
  onFormSheet,
  pendingReveal,
  pendingDay = null,
  onPendingDayDone,
  onRevealDone,
  personalDrafts,
  onSavePersonal,
  onDeletePersonal,
  onRetimePersonal,
}: Props) {
  /**
   * The event being written, if any. One piece of state for both entry points:
   * the header button and a click on an empty slot. `placement` only decides
   * where the form is drawn.
   */
  const [draft, setDraft] = useState<PersonalDraft | null>(null);
  const [placement, setPlacement] = useState<'dialog' | 'panel' | 'sheet'>('dialog');
  /** Set when Delete is pressed; the confirmation reads from it. */
  const [confirmDelete, setConfirmDelete] = useState<PersonalDraft | null>(null);

  const [colorBy, setColorBy] = useState<ColorKey>('none');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(true);
  /** What the right sidebar holds, by the same rule as Explore's. */
  const holds = rightSidebarHolds(calloutOpen, panelOpen);
  const [exportOpen, setExportOpen] = useState(false);
  /** Share My Schedule's menu, open under its button. */
  const [shareAnchor, setShareAnchor] = useState<HTMLElement | null>(null);
  const [exportScope, setExportScope] = useState<'all' | 'scheduled'>('all');
  const [clearOpen, setClearOpen] = useState(false);
  /** The id currently being revealed, and whether its fade-in has started. */
  const [revealing, setRevealing] = useState<string | null>(null);
  /** How the open day was reached. A swiped-to day keeps the page's scroll. */
  const [arrival, setArrival] = useState<'tap' | 'swipe'>('tap');
  /** The phone's pinched hour spacing, shared by the open day and its neighbours. */
  const [dayHourPx, setDayHourPx] = useState(74);

  /**
   * Arriving from "Go to My Schedule": open the day after a beat, let the day
   * view scroll the event to center, and hold the card hidden until it does.
   * DayDetail reports back once the scroll settles, and the card fades in.
   */
  /** Where the open day lands, when it was opened from a mini day. */
  const [landAt, setLandAt] = useState<{ dayKey: string; minute: number } | null>(null);
  useEffect(() => {
    if (!pendingDay) return;
    setArrival('tap');
    setOpenDay(pendingDay.dayKey);
    setLandAt(pendingDay);
    onPendingDayDone?.();
    // Once per arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDay]);
  // Only that arrival: the day opened again later lands as any day does.
  if (landAt && openDay !== landAt.dayKey) setLandAt(null);

  useEffect(() => {
    if (!pendingReveal) return;
    setRevealing(pendingReveal.id);
    const t = setTimeout(() => {
      setArrival('tap');
      setOpenDay(pendingReveal.dayKey);
    }, 1000);
    return () => clearTimeout(t);
  }, [pendingReveal]);

  /**
   * The ✕ on a card.
   *
   * A published event leaves whichever of the two lists holds it, and can be
   * added back from Explore. A personal event is in no list — it is on the
   * schedule by existing — so ✕ can only mean delete, and deleting the only
   * copy of something asks first.
   */
  const removeCard = useCallback(
    (id: string) => {
      const own = personalDrafts.find((d) => d.id === id);
      if (own) {
        setConfirmDelete(own);
        return;
      }
      onRemove(id);
    },
    [personalDrafts, onRemove],
  );

  const mine = useMemo(() => [...scheduled, ...saved], [scheduled, saved]);
  /** What is scheduled on the open day, for MAP MY DAY. */
  const openDayScheduled = useMemo(
    () => (openDay ? scheduled.filter((e) => localDate(e) === openDay) : []),
    [scheduled, openDay],
  );

  const eventsByDay = useMemo(() => {
    const map = new Map<string, Opportunity[]>();
    for (const e of mine) {
      const day = localDate(e);
      const list = map.get(day);
      if (list) list.push(e);
      else map.set(day, [e]);
    }
    return map;
  }, [mine]);

  /**
   * The eight core days (20th–27th), plus any pre- or post-week day the user
   * actually has something on. Those extra days sit outside the visible width
   * and are reached by scrolling sideways, so they never shrink the core week.
   */
  const days = useMemo(() => {
    // The day being written into counts as a day the user has something on,
    // even before it is saved — otherwise moving a draft to the 13th would
    // send the view to a day the week does not contain.
    const outside = new Set([...eventsByDay.keys(), draft?.dayKey].filter(Boolean) as string[]);
    const extras = [...outside]
      .filter((k) => k < CORE_DAYS[0].key || k > CORE_DAYS[CORE_DAYS.length - 1].key)
      .sort();
    const all = [...extras, ...CORE_DAYS.map((d) => d.key)].sort();
    return all.map((key) => {
      const core = CORE_DAYS.find((d) => d.key === key);
      const { full, weekday } = longDate(key);
      return { key, label: core ? core.label : full.replace(/^\w+ /, ''), weekday };
    });
  }, [eventsByDay, draft?.dayKey]);

  const totals = useWeekStats(scheduled, saved, attendance.trims);

  const mobile = useIsMobile();
  /**
   * The narrowest phones still in use — 360px and below, which is most of the
   * Android install base. The row of controls holds its line there by saying
   * less, rather than by wrapping or by clipping a word in half.
   */
  const tiny = useMediaQuery('(max-width: 369.95px)');
  const legend = legendFor(colorBy);

  const dayIndex = openDay ? days.findIndex((d) => d.key === openDay) : -1;

  /**
   * The phone's personal event form, half up under the day like the callout,
   * with the event drawn into the day as it is written. Kept on screen while it
   * slides away.
   */
  const formSheetOpen = mobile && Boolean(draft) && placement === 'sheet';
  const [sheetDraft, setSheetDraft] = useState<PersonalDraft | null>(null);
  if (formSheetOpen && draft && draft !== sheetDraft) setSheetDraft(draft);
  const shownDraft = formSheetOpen ? draft : sheetDraft;
  const editingDraft = Boolean(shownDraft && personalDrafts.some((d) => d.id === shownDraft.id));

  /** Something a swipe would lose: typed into a new event, or changed in an existing one. */
  const draftHasInput = useMemo(() => {
    if (!draft) return false;
    const saved = personalDrafts.find((d) => d.id === draft.id);
    if (saved) return JSON.stringify(saved) !== JSON.stringify(draft);
    return Boolean(draft.title.trim() || draft.venue.trim() || draft.description.trim());
  }, [draft, personalDrafts]);

  useEffect(() => {
    onFormSheet?.(formSheetOpen);
  }, [formSheetOpen, onFormSheet]);

  /**
   * The sheet's own Cancel, for App: a view change, or the account page opening
   * over the app, must not leave a half-written event floating over the other
   * page. `setDraft(null)` is exactly what the sheet's ✕ does — `holdAtHalf` is
   * the unsaved-input handling, and it belongs to the drag, not to this.
   */
  useImperativeHandle(ref, () => ({ cancelFormSheet: () => setDraft(null) }), []);

  /** A change to the draft; on a phone the day view follows it to another date. */
  const changeDraft = (next: PersonalDraft) => {
    setDraft(next);
    if (mobile && next.dayKey && next.dayKey !== openDay) setOpenDay(next.dayKey);
  };

  /** Something is half up over the day: the callout, or the form. */
  const sheetUp = calloutPeek || formSheetOpen;

  /**
   * The row of buttons, which slides up and away while a sheet is half up.
   * A phone has no row to slide any more: Share is in the header, and Add and
   * the lens are round buttons that stay put under an open sheet.
   */
  const head = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const node = head.current;
    if (!node) return;
    const gap = parseFloat(getComputedStyle(node).marginBottom) || 0;
    node.style.transition = 'margin-top 380ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease';
    node.style.marginTop = sheetUp ? `-${node.offsetHeight + gap}px` : '';
    node.style.opacity = sheetUp ? '0' : '';
    node.style.pointerEvents = sheetUp ? 'none' : '';
  }, [sheetUp]);

  const handleExport = () => {
    const ics = buildIcs({
      scheduled,
      saved: exportScope === 'all' ? saved : [],
    });
    downloadIcs(ics);
    setExportOpen(false);
  };

  /**
   * One day, in detail.
   *
   * Built here rather than inline because the two layouts put it in different
   * places: a phone drops it into the page under the week, and everything
   * wider floats it over the week as an overlay.
   *
   * `neighbor` draws a day beside the open one for the phone's swipe: inert,
   * with nothing selected, previewed, revealed or being written into it.
   */
  const dayView = (key: string, neighbor = false) => {
    const index = days.findIndex((d) => d.key === key);
    return (
  <DayDetail
              dayKey={key}
              events={eventsByDay.get(key) ?? []}
              preview={neighbor}
              landOnOpen={arrival === 'tap'}
              landAt={!neighbor && landAt?.dayKey === key ? landAt.minute : null}
              home={home.home}
              hourPx={mobile ? dayHourPx : undefined}
              onHourPx={mobile ? setDayHourPx : undefined}
              savedIds={savedIds}
              colorBy={colorBy}
              canPrev={index > 0}
              canNext={index >= 0 && index < days.length - 1}
              onPrev={() => setOpenDay(days[index - 1].key)}
              onNext={() => setOpenDay(days[index + 1].key)}
              onClose={() => setOpenDay(null)}
              onOpenEvent={onOpenEvent}
              calloutPeek={sheetUp}
              onRemove={removeCard}
              onToggleList={onToggleList}
              legChoices={legChoices.choices}
              onLegStart={legChoices.setStart}
              onLegMode={legChoices.setMode}
              trims={attendance.trims}
              onTrim={attendance.setEdge}
              draft={!neighbor && (placement === 'panel' || placement === 'sheet') ? draft : null}
              /**
               * Changing the date in the panel form takes the day view with it.
               *
               * The form is anchored beside one day and draws the draft into it,
               * which is the whole reason it is a panel rather than a dialog. A
               * date typed into it that the column does not then show would be a
               * form describing one day next to a picture of another.
               */
              onDraftChange={(next) => {
                setDraft(next);
                if (next.dayKey && next.dayKey !== openDay) setOpenDay(next.dayKey);
              }}
              onDraftSave={() => {
                if (draft) onSavePersonal(draft);
                setDraft(null);
              }}
              onDraftCancel={() => setDraft(null)}
              onDraftDelete={
                draft && personalDrafts.some((d) => d.id === draft.id)
                  ? () => setConfirmDelete(draft)
                  : undefined
              }
              onEmptySlot={(dayKey, minutes) => {
                const start = slotFor(minutes);
                if (mobile) {
                  // A phone has no room beside the day for a panel: the form
                  // opens half up underneath it. Already writing one? It moves
                  // here, keeping its length and whatever has been typed.
                  setDraft((current) =>
                    current
                      ? { ...current, dayKey, startMinutes: start, endMinutes: start + (current.endMinutes - current.startMinutes) }
                      : emptyDraft(dayKey, start),
                  );
                  setPlacement('sheet');
                  return;
                }
                setDraft(emptyDraft(dayKey, start));
                setPlacement('panel');
              }}
              onRetimePersonal={onRetimePersonal}
              onEditPersonal={(event) => {
                const found = personalDrafts.find((d) => d.id === event.id);
                if (!found) return;
                setDraft(found);
                // A phone has no room beside the day for a panel: the form
                // opens half up underneath it instead.
                setPlacement(mobile ? 'sheet' : 'panel');
              }}
              revealingId={neighbor ? null : revealing}
              onRevealDone={() => {
                setRevealing(null);
                onRevealDone();
              }}
            />
    );
  };
  const dayDetail = openDay ? dayView(openDay) : null;

  /** The week's totals, the color key and the travel footnote. */
  const stats = (
    <>
      <SectionLabel>THIS WEEK</SectionLabel>
      {/* The account page's longer labels, here too, so the same four numbers
          are called the same thing on both screens (Sam, Sept 19). "Days
          covered" is gone from the app, not just from the account page. */}
      {WEEK_STAT_ROWS.map(({ key, label }) => (
        <Stat key={key}>
          <span style={{ opacity: 0.75 }}>{label}</span>
          <strong style={{ color: isWarningStat(key) && totals[key] ? '#ef3d23' : undefined }}>
            {totals[key]}
          </strong>
        </Stat>
      ))}

      <Box sx={{ mt: 2.5 }}>
        <DangerBtn onClick={() => setClearOpen(true)} disabled={mine.length === 0}>
          Clear My Schedule
        </DangerBtn>
      </Box>

      {legend.length > 0 && (
        <>
          <SectionLabel style={{ marginTop: 26 }}>COLOR KEY</SectionLabel>
          {legend.map((row) => (
            <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 0.5, background: row.color }} />
              <Typography sx={{ fontSize: 12 }}>{row.label}</Typography>
            </Box>
          ))}
        </>
      )}

      <Typography sx={{ fontSize: 11, opacity: 0.45, mt: 3, lineHeight: 1.5 }}>
        Travel times are estimates from neighborhood to neighborhood, not routed
        directions. Add buffer for security lines and UNGA street closures.
      </Typography>
    </>
  );

  /**
   * Add a personal event, from wherever it is pressed.
   *
   * A phone's form sits under a day, so there has to be one: the day already
   * open, or the first of the week, opened. Everything wider opens the dialog,
   * on the open day when there is one rather than at the fixed first day — the
   * day on screen is the day being added to.
   */
  const addPersonal = () => {
    // The first day of Climate Week, not the first column: a pre-week day is
    // outside the range the date field offers.
    const day = openDay ?? CORE_DAYS[0].key;
    if (mobile) {
      if (!openDay) {
        setArrival('tap');
        setOpenDay(day);
      }
      setDraft(emptyDraft(day, 9 * 60));
      setPlacement('sheet');
      return;
    }
    setDraft(emptyDraft(day, 9 * 60));
    setPlacement('dialog');
  };

  const addButton = (keep: boolean) => (
    <ExportBtn $keep={keep} onClick={addPersonal} disabled={Boolean(draft)}>
      <PlusGlyph />
      {tiny && !keep ? 'Add event' : 'Add a personal event'}
    </ExportBtn>
  );

  /** Never disabled: the link and the feed fill in as events are added. */
  const shareButton = (keep: boolean) => (
    <ExportBtn
      $keep={keep}
      onClick={(e) => setShareAnchor(e.currentTarget)}
      aria-haspopup="menu"
      aria-expanded={Boolean(shareAnchor)}
    >
      <Icon name="ui:arrowUpRight" size={11} />
      {tiny && !keep ? 'Share' : 'Share My Schedule'}
    </ExportBtn>
  );

  /**
   * How much room the top strip's link area has, from 900px up. It decides
   * whether Add and Share fit there labelled, as icons, or not at all. The
   * area's width comes from the page, never from what is in it, so measuring it
   * cannot chase its own tail.
   */
  const [stripRoom, setStripRoom] = useState(Number.POSITIVE_INFINITY);
  const watchStrip = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setStripRoom(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  /** Labelled: 178 + 12 + 164. As icons: 34 + 12 + 34. */
  const stripTier = !chrome
    ? 'row'
    : stripRoom >= 354
      ? 'labelled'
      : stripRoom >= 80
        ? 'icons'
        : 'row';

  const stripButtons = chrome && stripTier !== 'row' && (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      {stripTier === 'labelled' ? (
        <>
          {addButton(true)}
          {shareButton(true)}
        </>
      ) : (
        <>
          <Tooltip title="Add a personal event">
            <RoundStripBtn onClick={addPersonal} aria-label="Add a personal event" disabled={Boolean(draft)}>
              <PlusGlyph />
            </RoundStripBtn>
          </Tooltip>
          <Tooltip title="Share My Schedule">
            <RoundStripBtn
              onClick={(e) => setShareAnchor(e.currentTarget)}
              aria-label="Share My Schedule"
              aria-haspopup="menu"
              aria-expanded={Boolean(shareAnchor)}
            >
              <Icon name="ui:arrowUpRight" size={13} />
            </RoundStripBtn>
          </Tooltip>
        </>
      )}
    </Box>
  );

  /**
   * Inert while the form is open: the form is drawn over this corner of the
   * screen, and a button that still answers a click from under it closes the
   * panel by accident.
   */
  const panelToggle = !mobile && (
    <SidebarToggle
      side="right"
      open={holds !== null}
      what="panel"
      onToggle={() => {
        if (holds === 'callout') {
          onCloseCallout?.();
          setPanelOpen(false);
        } else setPanelOpen((v) => !v);
      }}
      disabled={Boolean(draft)}
    />
  );

  const main = (
    <Main>
      {/* A phone has no row: Share is in the header, Add and the lens are the
          two circles, and the title is the page. */}
      {!phoneChrome && (
        <Head ref={head} style={chrome ? { paddingLeft: 50 } : undefined}>
          {/* Below 900px it keeps the row's first place: at the other end it
              would sit on the open day's heading. */}
          {!chrome && panelToggle}

          {!openDay && !mobile && <Title>My Schedule</Title>}

          {!mobile && <Box sx={{ flex: 1 }} />}

          <Actions>
            {/* From 900px up these are in the top strip unless there is no room
                for them there, and then they come back here — still shown with
                a day open, which the overlay never covers. Below 900px they
                have always lived here. */}
            {(!openDay || mobile) && !chrome && (
              <>
                {addButton(false)}
                {/* Share My Schedule replaces Export to calendar (D13); the
                    download is its third item. */}
                {shareButton(false)}
              </>
            )}
            {chrome && stripTier === 'row' && (
              <>
                {addButton(false)}
                {shareButton(false)}
              </>
            )}

            {/* Pushed right by a margin rather than a spacer: a spacer is a flex
                item, and the gap either side of it was the eight pixels that put
                the lens on a line of its own. */}
            <Tooltip title="Color-code my week">
              <IconBtn
                style={mobile ? { marginLeft: 'auto', flexShrink: 0 } : undefined}
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                aria-label="Color-code my week"
                aria-haspopup="menu"
              >
                <Icon name={colorBy === 'none' ? 'ui:filter' : 'ui:filterOn'} size={22} />
              </IconBtn>
            </Tooltip>
          </Actions>

          {/* From 900px up, the panel's own corner. */}
          {chrome && panelToggle}
        </Head>
      )}

      {/* Lifted out of the row, which a phone no longer draws: the colour menu
          keeps working from the round button, and exactly one ShareMenu stays
          mounted wherever Share is drawn — it is what reopens a dialog after a
          login, and `shareAnchor` is this view's state. */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        // From the round button the menu has to open upward and to the left:
        // there is nothing below it but the screen's edge.
        anchorOrigin={phoneChrome ? { vertical: 'top', horizontal: 'right' } : undefined}
        transformOrigin={phoneChrome ? { vertical: 'bottom', horizontal: 'right' } : undefined}
        slotProps={{ paper: { sx: { background: SIDEBAR_BG, color: BRAND.parchment, minWidth: 250 } } }}
      >
        {COLOR_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.id}
            selected={colorBy === opt.id}
            onClick={() => {
              setColorBy(opt.id);
              setMenuAnchor(null);
            }}
            sx={{ display: 'block', py: 1 }}
          >
            <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{opt.label}</Typography>
            <Typography sx={{ fontSize: 11.5, opacity: 0.6 }}>{opt.hint}</Typography>
          </MenuItem>
        ))}
      </Menu>

      <ShareMenu
        anchor={shareAnchor}
        onClose={() => setShareAnchor(null)}
        onDownload={() => setExportOpen(true)}
        downloadDisabled={mine.length === 0}
      />

      {/* A phone's Share, drawn into App's header where the toggle used to be. */}
      {phoneChrome && shareSlot && createPortal(shareButton(true), shareSlot)}

      {/* Below 900px a sideways swipe here goes back to Explore, so this
          scroll container passes horizontal movement to the page. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          pr: 1,
          touchAction: { xs: 'pan-y', md: 'auto' },
          // Room under the day, so an evening event can still be scrolled up
          // into the middle of the space above the half-up callout; otherwise
          // a phone clears the toggle's band, so Clear My Schedule at the end
          // of the scroll is not sitting under it.
          pb: sheetUp && mobile ? '61vh' : phoneChrome ? TOGGLE_BAND : undefined,
        }}
      >
        <Centered>
          {mine.length === 0 && (
            <Box sx={{ py: mobile ? 3 : 10, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontFamily: FONTS.serif,
                  fontSize: mobile ? 21 : 24,
                  fontWeight: 700,
                  mb: 1,
                }}
              >
                Your week is empty
              </Typography>
              <Typography sx={{ opacity: 0.65, fontSize: mobile ? 13.5 : 14.5 }}>
                {/* The phone has no ⊕ to point at: the gesture is the button. */}
                {mobile
                  ? 'Head to Explore and swipe an event right to add it here. Swipe left to save one for later — those show up here too, at half opacity.'
                  : 'Head to Explore and add events with the ⊕ button. Anything you save for later shows up here too, at half opacity.'}
              </Typography>
            </Box>
          )}

          {/**
           * The empty week is still drawn on a phone.
           *
           * Eight columns with nothing in them are not a wasted screen: they
           * are the shape of the thing being filled in, and someone who has
           * seen them once knows where their first saved event will land.
           * A wide screen keeps the message on its own, as before.
           */}
          {(mine.length > 0 || mobile) && (
            <WeekGrid
              days={days}
              eventsByDay={eventsByDay}
              savedIds={savedIds}
              colorBy={colorBy}
              trims={attendance.trims}
              openDay={openDay}
              onOpenDay={(key) => {
                setArrival('tap');
                setOpenDay((current) => (current === key ? null : key));
              }}
            />
          )}

          {/* On a phone the day is part of this page, below the week. */}
          {mobile && openDay && (
            <DaySwipe
              prev={dayIndex > 0 ? { key: days[dayIndex - 1].key, node: dayView(days[dayIndex - 1].key, true) } : null}
              current={{ key: openDay, node: dayDetail }}
              next={
                dayIndex >= 0 && dayIndex < days.length - 1
                  ? { key: days[dayIndex + 1].key, node: dayView(days[dayIndex + 1].key, true) }
                  : null
              }
              onPrev={() => {
                setArrival('swipe');
                setOpenDay(days[dayIndex - 1].key);
              }}
              onNext={() => {
                setArrival('swipe');
                setOpenDay(days[dayIndex + 1].key);
              }}
            />
          )}

          {/* Nothing on the week says it can be tapped, so this does. */}
          {mobile && !openDay && (
            <Typography
              sx={{ textAlign: 'center', fontSize: 13.5, opacity: 0.72, mt: 3, px: 2 }}
            >
              Tap on a day above to view a detailed schedule.
            </Typography>
          )}

          {mobile && mine.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <DangerBtn onClick={() => setClearOpen(true)}>Clear My Schedule</DangerBtn>
            </Box>
          )}
        </Centered>
      </Box>

      {/* The day view takes its own drags: trims, travel blocks, cards. */}
      {!mobile && openDay && (
        <div data-own-swipe style={{ display: 'contents' }}>
          {dayDetail}
        </div>
      )}

    </Main>
  );

  return (
    <Wrap>
      {/* The two circles, in the page rather than fixed to the screen, so they
          slide with it on a page swipe. They never hide: not with a day open,
          not under a half-up sheet. The callout simply draws over them. */}
      {phoneChrome && (
        <>
          <Tooltip title="Color-code my week">
            <RoundBtn
              type="button"
              data-own-swipe
              aria-label="Color-code my week"
              aria-haspopup="menu"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              style={{ bottom: `calc(${TOGGLE_BAND} + 68px)` }}
            >
              <Icon name={colorBy === 'none' ? 'ui:filter' : 'ui:filterOn'} size={19.2} />
            </RoundBtn>
          </Tooltip>
          <RoundBtn
            type="button"
            data-own-swipe
            aria-label="Add a personal event"
            onClick={addPersonal}
            disabled={Boolean(draft)}
            style={{ bottom: TOGGLE_BAND }}
          >
            <BigPlusGlyph size={18.3} />
          </RoundBtn>
        </>
      )}

      {chrome ? (
        <CentreColumn>
          <TopStrip>
            <div>{chrome.lockup}</div>
            {/* Add and Share end the strip. About Us / Submit Feedback! join
                after them when the sidebar that holds them is closed, with
                StripLinks' own 24px gap — the way the Account link's two
                neighbours always behaved. */}
            <StripLinks ref={watchStrip}>
              {stripButtons}
              {!holds && chrome.links}
            </StripLinks>
          </TopStrip>
          <MainPad>{main}</MainPad>
        </CentreColumn>
      ) : (
        main
      )}

      {/* The phone has no panel at all: the week's totals are the thing a
          small screen can most afford to lose, and what is left is a week you
          can read at arm's length. */}
      {chrome ? (
        // From 900px up: one column for the totals and the callout, which
        // eases from one to the other.
        <RightColumn
          holds={holds}
          panel={
            panelOpen && (
              <RightSidebar>
                <SidebarTop style={{ padding: `0 ${RIGHT_SIDEBAR_X}px` }}>{chrome.links}</SidebarTop>
                <RightSidebarBody>
                  {stats}
                  {/* The open day's route, named, since nothing else here says
                      which day it is. Drawn with no day open too: with nothing
                      to map it is the hotel's offer alone, which is what a
                      week with nothing on it yet should still hold (Sept 19). */}
                  <MapMyDay
                    events={openDay ? openDayScheduled : []}
                    trims={attendance.trims}
                    home={home}
                    onOpenEvent={(event) => onOpenEvent(event)}
                    dayLabel={
                      openDay
                        ? `${longDate(openDay).weekday.slice(0, 3)}, ${longDate(openDay).full}`
                        : undefined
                    }
                  />
                </RightSidebarBody>
              </RightSidebar>
            )
          }
          callout={callout ?? null}
        />
      ) : (
        holds === 'panel' &&
        !mobile && (
          <ResizableSidebar side="right" defaultFraction={0.25} label="Resize the panel">
            {stats}
          </ResizableSidebar>
        )
      )}

      <ClearScheduleDialog
        open={clearOpen}
        onClose={() => setClearOpen(false)}
        counts={{ all: mine.length, scheduled: scheduled.length }}
        onClear={(scope) => {
          onClear(scope);
          setOpenDay(null);
        }}
      />

      <Dialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        slotProps={{
          paper: { sx: { background: SIDEBAR_BG, backgroundImage: 'none', borderRadius: 3, p: 3, minWidth: 340 } },
        }}
      >
        <Typography sx={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 19, mb: 0.5, color: BRAND.parchment }}>
          Export to calendar
        </Typography>
        <Typography sx={{ fontSize: 12.5, opacity: 0.65, mb: 2, color: BRAND.parchment }}>
          Downloads an .ics file. Google Calendar, Outlook and Apple Calendar all import it.
        </Typography>

        {(
          [
            ['all', `Everything (${mine.length} events)`, 'Saved-for-later events import as tentative'],
            ['scheduled', `My Schedule only (${scheduled.length} events)`, 'Just what you committed to'],
          ] as const
        ).map(([value, label, hint]) => (
          <Box
            key={value}
            onClick={() => setExportScope(value)}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              p: 1,
              borderRadius: 2,
              cursor: 'pointer',
              color: BRAND.parchment,
              '&:hover': { background: 'rgba(243,238,231,0.06)' },
            }}
          >
            <Radio
              checked={exportScope === value}
              size="small"
              sx={{ color: 'rgba(243,238,231,0.6)', p: 0.4, '&.Mui-checked': { color: BRAND.parchment } }}
            />
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{label}</Typography>
              <Typography sx={{ fontSize: 11.5, opacity: 0.6 }}>{hint}</Typography>
            </Box>
          </Box>
        ))}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button onClick={() => setExportOpen(false)} sx={{ color: 'rgba(243,238,231,0.7)', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            sx={{
              background: BRAND.parchment,
              color: BRAND.mocha,
              fontWeight: 700,
              borderRadius: 999,
              px: 3,
              '&:hover': { background: '#fff' },
            }}
          >
            Download .ics
          </Button>
        </Box>
      </Dialog>

      {mobile && (
        <HalfSheet
          open={formSheetOpen}
          contentKey={draft?.id ?? null}
          onClose={() => setDraft(null)}
          onClosed={() => setSheetDraft(null)}
          holdAtHalf={draftHasInput}
        >
          {shownDraft && (
            <FormSheet>
              <FormBar data-callout-chrome>
                <BarClose type="button" aria-label="Cancel" onClick={() => setDraft(null)}>
                  <svg width={16} height={16} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                    <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
                  </svg>
                </BarClose>
                <FormBarTitle>{editingDraft ? 'Edit personal event' : 'New personal event'}</FormBarTitle>
                <BarSave
                  type="button"
                  disabled={!isComplete(shownDraft)}
                  onClick={() => {
                    if (!draft) return;
                    onSavePersonal(draft);
                    setDraft(null);
                  }}
                >
                  Save
                </BarSave>
              </FormBar>
              <FormScroll data-callout-scroll>
                <PersonalEventForm
                  inSheet
                  draft={shownDraft}
                  onChange={changeDraft}
                  onSave={() => {
                    if (!draft) return;
                    onSavePersonal(draft);
                    setDraft(null);
                  }}
                  onCancel={() => setDraft(null)}
                  onDelete={editingDraft && draft ? () => setConfirmDelete(draft) : undefined}
                />
              </FormScroll>
            </FormSheet>
          )}
        </HalfSheet>
      )}

      {/* The header button's placement. The panel placement lives inside the
          day view, where it can sit beside the column it is adding to. */}
      <Dialog
        open={Boolean(draft) && placement === 'dialog'}
        onClose={() => setDraft(null)}
        // The date row needs the width: "Wednesday, September 23" beside two
        // times does not fit in a narrow dialog.
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: { background: SIDEBAR_BG, backgroundImage: 'none', borderRadius: 3, p: 3 },
          },
        }}
      >
        {draft && (
          <PersonalEventForm
            heading={personalDrafts.some((d) => d.id === draft.id) ? 'Edit personal event' : 'New personal event'}
            draft={draft}
            onChange={setDraft}
            onSave={() => {
              onSavePersonal(draft);
              setDraft(null);
            }}
            onCancel={() => setDraft(null)}
            onDelete={
              personalDrafts.some((d) => d.id === draft.id)
                ? () => setConfirmDelete(draft)
                : undefined
            }
          />
        )}
      </Dialog>

      {/**
       * Deleting asks first. Every other card on this schedule is a reference
       * into a dataset we still hold, so removing one loses nothing; this is
       * the only thing here the user typed, and the only thing an undo toast
       * could not really get back once the session ends.
       */}
      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs"
        slotProps={{
          paper: {
            sx: { background: SIDEBAR_BG, backgroundImage: 'none', borderRadius: 3, p: 3 },
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: FONTS.display,
            fontWeight: 700,
            fontSize: 18,
            mb: 1,
            color: BRAND.parchment,
          }}
        >
          Delete “{confirmDelete?.title.trim()}”?
        </Typography>
        <Typography sx={{ fontSize: 13, opacity: 0.7, mb: 2.5, color: BRAND.parchment }}>
          This one is yours rather than ours, so we cannot put it back.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.2, justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setConfirmDelete(null)}
            sx={{ color: 'rgba(243,238,231,0.7)', fontWeight: 600 }}
          >
            Keep it
          </Button>
          <Button
            onClick={() => {
              if (confirmDelete) onDeletePersonal(confirmDelete.id);
              setConfirmDelete(null);
              setDraft(null);
            }}
            sx={{
              background: '#ef3d23',
              color: BRAND.parchment,
              fontWeight: 700,
              borderRadius: 999,
              px: 3,
              '&:hover': { background: '#ff5236' },
            }}
          >
            Delete
          </Button>
        </Box>
      </Dialog>
    </Wrap>
  );
}
